export type CheckStatus = 'all' | 'open' | 'closed' | 'cancelled'
export type DateField = 'all' | 'opened_at' | 'closed_at' | 'updated_at'

const STATUSES: readonly CheckStatus[] = ['all', 'open', 'closed', 'cancelled']
const DATE_FIELDS: readonly DateField[] = ['all', 'opened_at', 'closed_at', 'updated_at']

function isValidDate(d: unknown): d is Date {
  return d instanceof Date && !Number.isNaN(d.getTime())
}

/** A fresh [start, end] tuple for the current day. */
export function todayRange(): [Date, Date] {
  return [new Date(), new Date()]
}

/**
 * Coerce any input into a guaranteed [Date, Date]:
 *   valid 2-tuple of Dates → passed through
 *   single valid Date d    → [d, d]
 *   null / malformed / junk → todayRange()
 */
export function normalizeDateRange(input: unknown): [Date, Date] {
  if (Array.isArray(input)) {
    if (input.length === 2 && isValidDate(input[0]) && isValidDate(input[1])) {
      return [input[0], input[1]]
    }
    return todayRange()
  }
  if (isValidDate(input)) {
    return [input, input]
  }
  return todayRange()
}

/** yyyy-MM-dd in local time. */
export function formatDateParam(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function isToday(date: Date): boolean {
  const today = new Date()
  return date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate()
}

function parseQueryDate(v: unknown): Date | null {
  if (typeof v !== 'string') return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return null
  const d = new Date(`${v}T00:00:00`)
  return isValidDate(d) ? d : null
}

/** URL query (from/to as 'yyyy-MM-dd') → [Date, Date]; missing/invalid → today. */
export function parseDateRangeFromQuery(q: { from?: unknown, to?: unknown }): [Date, Date] {
  const from = parseQueryDate(q?.from)
  const to = parseQueryDate(q?.to)
  if (from && to) return [from, to]
  return todayRange()
}

export function dateRangeToQuery(range: [Date, Date]): { from: string, to: string } {
  return { from: formatDateParam(range[0]), to: formatDateParam(range[1]) }
}

export function validateStatus(v: unknown): CheckStatus {
  return STATUSES.includes(v as CheckStatus) ? (v as CheckStatus) : 'all'
}

export function validateDateField(v: unknown): DateField {
  return DATE_FIELDS.includes(v as DateField) ? (v as DateField) : 'all'
}

export function validatePaymentMethodId(v: unknown): number | 'all' {
  if (v === 'all' || v === undefined || v === null) return 'all'
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : Number.NaN
  return Number.isInteger(n) && n > 0 ? n : 'all'
}

export function parsePaymentMethodFromQuery(q: { payment_method?: unknown }): number | 'all' {
  return validatePaymentMethodId(q?.payment_method)
}

export function paymentMethodToQuery(methodId: number | 'all'): { payment_method?: string } {
  return methodId === 'all' ? {} : { payment_method: String(methodId) }
}

export function resolvePaymentMethodId(name: string, methods: { id: number, name: string }[]): number | null {
  const match = methods.find(m => m.name.toLowerCase() === name.toLowerCase())
  return match?.id ?? null
}

export function checkMatchesPaymentMethod(
  check: { payment_method_id?: number, tenders?: { method_id: number | null }[] },
  methodId: number | 'all',
): boolean {
  if (methodId === 'all') return true
  if (check.payment_method_id === methodId) return true
  return !!check.tenders?.some(tender => tender.method_id === methodId)
}
